import { CHANNELS } from "../../shared/channels";
import { TaskEntry, TaskStatus } from "../../shared/types";
import { call } from "../bridge";
import { cloneTemplate, fillFields } from "../dom";
import { t } from "../i18n";
import { formatDate } from "../utils";
import { completeTask, openTaskEditor } from "./task_editor";
import { STATUS_LABEL, buildStatusChip, buildTrashButton, setStatus } from "./task_actions";

// builds one task row, shared by the today and tasks views

// a task someone shares with you shows the same but can't be ticked or edited.
// `status` is set where the row may show it, `interactive` adds the status chip and the trash can
export function buildTaskItem(task: TaskEntry, readOnly = false, status: TaskStatus = "todo", interactive = false): HTMLElement {
  const meta = [!interactive && status !== "todo" && t(STATUS_LABEL[status]), task.board, task.state, task.priority && t(task.priority), task.dueDate && t("due {date}", { date: formatDate(task.dueDate) })]
    .filter(Boolean)
    .join(" · ");

  const item = fillFields(cloneTemplate("template-task"), { source: task.source, title: task.title, meta });
  item.querySelector(".badge")?.classList.add(task.source);
  item.title = task.description;

  // plane tasks open in the browser, chix tasks open the sticky editor
  item.classList.add(`from-${task.source}`, `status-${status}`);
  if (task.url) item.addEventListener("click", () => call(CHANNELS.openExternal, task.url));
  if (readOnly) {
    item.querySelector('[data-action="done"]')?.remove();
    return item;
  }
  if (task.source === "chix") {
    item.addEventListener("click", () => openTaskEditor(task));
    item.querySelector('[data-action="done"]')?.addEventListener("click", (event) => {
      event.stopPropagation();
      completeTask(task);
    });
  }
  if (interactive) {
    item.querySelector(".task-actions")?.before(buildStatusChip(task, status, (next) => void setStatus(task, next)));
    item.append(buildTrashButton(task, () => item));
  }
  return item;
}

// due soonest first, then urgent priorities, then oldest
const priorityRank: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

export function sortTasks(tasks: TaskEntry[]): TaskEntry[] {
  return [...tasks].sort((first, second) => {
    const firstDue = first.dueDate || "9999";
    const secondDue = second.dueDate || "9999";
    if (firstDue !== secondDue) return firstDue.localeCompare(secondDue);
    const rankGap = (priorityRank[first.priority] ?? 4) - (priorityRank[second.priority] ?? 4);
    if (rankGap !== 0) return rankGap;
    return first.createdAt.localeCompare(second.createdAt);
  });
}
